import React from 'react';
import classNames from 'classnames';
import { TiminatorTableColumn } from './types';
import styles from './table-v2.module.scss';
import TableHeader from './table-header';
import TableCell from './table-cell';
import { useTableController } from './hooks/useTableController';

interface Props<Entity> {
    columns: TiminatorTableColumn<Entity>[];
    data: Entity[] | null;
    onRowClick?: (item: Entity) => void;
    hasMore?: boolean;
    onLoadMore?: () => void;
    isLoadingMore?: boolean;
    desktopMinWidth?: string | number;
}

// Renders a responsive table with sticky headers, infinite scroll, and optional sorting.
export default function TiminatorTableV2<Entity>(props: Props<Entity>) {
    const {columns, data, onRowClick, hasMore, onLoadMore, isLoadingMore, desktopMinWidth} = props;
    const clickable = !!onRowClick;
    const {
        isDesktop,
        sentinelRef,
        viewportRef,
        contentRef,
        scrollbarRef,
        contentWidth,
        showScrollbar,
        scrollbarStyle,
        handleScrollbarScroll,
        handlePointerDown,
        handlePointerMove,
        endPointerDrag,
        handleViewportWheel,
        sortConfig,
        handleSortToggle,
        renderedData,
        resolveRowKey,
        gridTemplateColumns,
        tableContentStyle,
        handleRowClick,
    } = useTableController({
        columns,
        data,
        onRowClick,
        hasMore,
        onLoadMore,
        isLoadingMore,
        desktopMinWidth,
    });

    return (
        <div className={styles.tableContainer}>
            <div
                ref={viewportRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={endPointerDrag}
                onPointerCancel={endPointerDrag}
                onWheel={handleViewportWheel}
                className={classNames(styles.tableViewport, {
                    [styles.tableViewportWithBottomScrollbar]: showScrollbar,
                    [styles.tableViewportScrollable]: showScrollbar,
                })}>
                <div
                    ref={contentRef}
                    className={styles.tableContent}
                    style={tableContentStyle}>
                    {!isDesktop ? null : (
                        <TableHeader
                            columns={columns}
                            gridTemplateColumns={gridTemplateColumns}
                            sortConfig={sortConfig}
                            onSortClick={handleSortToggle}
                        />
                    )}

                    <div className={styles.tableBody}>
                        {renderedData.map((item: Entity, index: number) => {
                            const rowKey = resolveRowKey(item, index);
                            const isLastRow = index === renderedData.length - 1;

                            return (
                                <div
                                    key={rowKey}
                                    onClick={clickable ? () => handleRowClick(item) : undefined}
                                    className={classNames(styles.tableRow, {
                                        [styles.tableRowLast]: isLastRow,
                                        [styles.tableRowClickable]: clickable,
                                    })}
                                    style={{ display: 'grid', gridTemplateColumns }}>
                                    {columns.map((column) => {
                                        const value = (item as Record<string, React.ReactNode>)[column.id];
                                        return (
                                            <TableCell
                                                key={column.id}
                                                column={column}
                                                item={item}
                                                value={value}
                                            />
                                        );
                                    })}
                                </div>
                            );
                        })}
                        <div ref={sentinelRef} style={{ height: 1 }} />
                    </div>
                </div>
            </div>
            {showScrollbar ? (
                <div
                    ref={scrollbarRef}
                    aria-hidden='true'
                    className={styles.tableBottomScrollbar}
                    style={scrollbarStyle}
                    onScroll={handleScrollbarScroll}>
                    <div
                        className={styles.tableBottomScrollbarContent}
                        style={{ width: contentWidth }}
                    />
                </div>
            ) : null}
        </div>
    );
}
